// CHANGE: Removed all debug console statements for production
// ErrorBoundary component - catches unhandled JavaScript errors in the React tree
// Prevents white screen of death by displaying a fallback UI with reload option
// CREATED: Global error boundary wrapping AppContent to handle runtime crashes gracefully

import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { captureException } from '../lib/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureException(error, {
      react: { componentStack: errorInfo.componentStack },
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-5">
            <AlertTriangle size={28} className="text-red-500" />
          </div>

          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Une erreur est survenue
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            L'application a rencontre un probleme inattendu. Vous pouvez recharger la page pour reprendre.
          </p>

          {import.meta.env.DEV && this.state.error && (
            <div className="mb-6 text-left bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-40 overflow-y-auto">
              <p className="text-xs font-mono text-red-700 break-words">
                {this.state.error.message}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={this.handleReload}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none"
            >
              <RefreshCw size={16} />
              Recharger la page
            </button>

            <button
              onClick={this.handleReset}
              className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg border border-gray-200 transition-colors duration-200 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none"
            >
              Reprendre sans recharger
            </button>
          </div>
        </div>
      </div>
    );
  }
}
